import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiCall } from '@/utils/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowRight,
  Calendar,
  Eye,
  MessageSquare,
  Mail,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

interface SellerComparison {
  kommo_user_id: number;
  name: string;
  is_active: boolean;
  lead_count: number;
  won_count: number;
  conversion_rate: number;
  note_count: number;
  email_count: number;
  avg_response_hours: number | null;
}

const PERIODS = [
  { value: '30d', label: '30 jours' },
  { value: '90d', label: '90 jours' },
  { value: '6m', label: '6 mois' },
  { value: '1y', label: '1 an' },
];

const getResponseBadge = (hours: number | null) => {
  if (hours === null) return { label: '-', className: 'text-slate-400' };
  if (hours <= 2) return { label: `${hours}h`, className: 'text-emerald-600 font-semibold' };
  if (hours <= 6) return { label: `${hours}h`, className: 'text-amber-600 font-semibold' };
  return { label: `${hours}h`, className: 'text-red-500 font-semibold' };
};

const KommoSellers: React.FC = () => {
  const { token } = useAuth();
  const [data, setData] = useState<SellerComparison[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('90d');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await apiCall<{ success: boolean; data: SellerComparison[] }>(
          token,
          `/kommo/analytics/sellers/comparison?period=${period}`
        );
        if (res.success) setData(res.data);
      } catch (err) {
        toast.error('Erreur lors du chargement des donnees commerciaux');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [period, token]);

  // Sort sellers by lead_count for the chart
  const sortedSellers = [...data].sort((a, b) => b.lead_count - a.lead_count);

  return (
    <div className="px-4 py-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white p-5 rounded-xl border border-slate-200">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
            Performance Commerciaux
          </h1>
          <p className="text-sm text-slate-500 font-medium mt-0.5">
            Comparaison des performances de tous les commerciaux
          </p>
        </div>
        <SearchableSelect
          value={period}
          onValueChange={setPeriod}
          className="w-[140px]"
          icon={<Calendar className="h-3.5 w-3.5 text-slate-400" />}
          options={PERIODS.map((p) => ({ value: p.value, label: p.label }))}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <>
          {/* Leads & Conversions par commercial */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-base font-bold text-slate-900">Leads & Conversions par commercial</CardTitle>
              <CardDescription className="text-xs text-slate-500">
                Comparaison leads recus vs leads convertis (tous les commerciaux)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sortedSellers}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10 }}
                      stroke="#94a3b8"
                      interval={0}
                      angle={-30}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                    <Legend />
                    <Bar dataKey="lead_count" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Leads" />
                    <Bar dataKey="won_count" fill="#22c55e" radius={[4, 4, 0, 0]} name="Convertis" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Comparison Table */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-base font-bold text-slate-900">Tableau comparatif</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Commercial</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Leads</TableHead>
                      <TableHead className="text-right">Convertis</TableHead>
                      <TableHead className="text-right">Taux conv.</TableHead>
                      <TableHead className="text-right">Notes</TableHead>
                      <TableHead className="text-right">Emails</TableHead>
                      <TableHead className="text-right">Temps rep.</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.map((seller) => {
                      const resp = getResponseBadge(seller.avg_response_hours);
                      return (
                        <TableRow key={seller.kommo_user_id} className="hover:bg-slate-50/50">
                          <TableCell className="font-medium">{seller.name}</TableCell>
                          <TableCell>
                            <Badge variant={seller.is_active ? 'default' : 'secondary'}>
                              {seller.is_active ? 'Actif' : 'Inactif'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">{seller.lead_count.toLocaleString('fr-FR')}</TableCell>
                          <TableCell className="text-right font-mono">{seller.won_count.toLocaleString('fr-FR')}</TableCell>
                          <TableCell className="text-right font-mono font-semibold text-emerald-600">{seller.conversion_rate}%</TableCell>
                          <TableCell className="text-right font-mono text-slate-500">
                            <span className="flex items-center justify-end gap-1">
                              <MessageSquare className="h-3 w-3 text-slate-300" />
                              {seller.note_count.toLocaleString('fr-FR')}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono text-slate-500">
                            <span className="flex items-center justify-end gap-1">
                              <Mail className="h-3 w-3 text-slate-300" />
                              {seller.email_count.toLocaleString('fr-FR')}
                            </span>
                          </TableCell>
                          <TableCell className={cn('text-right font-mono', resp.className)}>
                            {resp.label}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button asChild variant="ghost" size="sm" title="Voir les evenements">
                                <Link to={`/kommo/sellers/${seller.kommo_user_id}/events`}>
                                  <Eye className="h-4 w-4" />
                                </Link>
                              </Button>
                              <Button asChild variant="ghost" size="sm" title="Performance detaillee">
                                <Link to={`/kommo/sellers/${seller.kommo_user_id}`}>
                                  <ArrowRight className="h-4 w-4" />
                                </Link>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default KommoSellers;
